import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
    triggerOtpThunk,
    verifyOtpThunk,
    logoutThunk,
} from "../features/auth/authThunk";

import { useCountdown } from "../hooks/useCountdown";
import OtpInputComponent from "../components/layout/OtpInputComponent";
import Button from "../components/ui/Button";

const VerifyOtp: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { mobileNumber, deviceId, uuid, verified, otpSent, consentHandle, error: authError } = useAppSelector(
        (s) => s.auth
    );
    const { error: consentError } = useAppSelector(
        (s) => s.consent
    );

    const error = authError || consentError;

    const [otp, setOtp] = useState("");
    const { time, reset } = useCountdown(30);

    // Redirect if no mobile number
    useEffect(() => {
        if (!mobileNumber) {
            navigate("/login");
        }
    }, [mobileNumber, navigate]);

    // Trigger OTP on mount if not already sent
    useEffect(() => {
        if (mobileNumber && !otpSent) {
            dispatch(triggerOtpThunk({ mobileNumber, deviceId, consentHandle }));
            reset();
        }
    }, [dispatch, mobileNumber, deviceId, consentHandle, reset, otpSent]);

    // Auto verify when OTP complete
    useEffect(() => {
        if (otp.length === 6) {
            dispatch(verifyOtpThunk({ mobileNumber, otp, deviceId, consentHandle }));
        }
    }, [otp, dispatch, mobileNumber, deviceId, consentHandle]);


    const maskedMobile = mobileNumber ? "XXXXXX" + mobileNumber.slice(-4) : "";
    const aaId = mobileNumber ? `${mobileNumber.slice(-4)}@camsfinserv` : "";

    const handleProceed = () => {
        if (verified) {
            // CAMS requires a WLOGOUT between OTP and LOGIN to prevent session conflicts
            // Use uuid from API response if available, fallback to deviceId
            dispatch(logoutThunk({ mobileNumber, deviceId: uuid || deviceId }));

            // Navigate immediately as logout is defensive
            navigate("/verifyPin");
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-fin-bg">
            {/* Main Content */}
            <div className="flex-1 flex flex-col p-6 items-center w-full max-w-sm mx-auto mt-10">

                {/* SMS Icon */}
                <div className="h-14 w-14 bg-fin-primary/10 rounded-full flex items-center justify-center mb-6 border border-fin-primary/20">
                    <svg className="w-6 h-6 text-fin-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                </div>

                <div className="text-center mb-8 w-full">
                    <h2 className="text-[17px] font-semibold text-white leading-snug">
                        Enter OTP sent to
                    </h2>
                    <p className="text-[17px] font-semibold text-white mt-1 tracking-wider">
                        +91 {maskedMobile}
                    </p>

                    <div className="mt-4 bg-fin-surface border border-fin-border py-2 px-4 rounded-lg inline-block">
                        <span className="text-xs text-fin-slate font-medium uppercase tracking-wider block mb-0.5">Your AA ID</span>
                        <span className="text-sm font-semibold text-white">{aaId}</span>
                    </div>
                </div>

                {/* Error Display */}
                {error && (
                    <div className="w-full mb-6 p-3 bg-red-900/10 border border-red-900/20 rounded-lg flex items-start gap-2 animate-in fade-in slide-in-from-top-1">
                        <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="text-sm text-red-400 font-medium">{error}</div>
                    </div>
                )}

                <div className="w-full flex justify-center mb-6">
                    <OtpInputComponent
                        value={otp}
                        onChange={(val) => setOtp(val)}
                    />
                </div>

                {!verified && (
                    <div className="text-center w-full mb-8">
                        {time === 0 ? (
                            <button
                                onClick={() => {
                                    dispatch(triggerOtpThunk({ mobileNumber, deviceId }));
                                    reset();
                                }}
                                className="text-sm font-semibold text-fin-primary hover:text-fin-400 underline underline-offset-2"
                            >
                                Resend Code
                            </button>
                        ) : (
                            <p className="text-sm text-fin-slate">
                                Resend code in <span className="font-mono font-bold text-white">{time}s</span>
                            </p>
                        )}
                    </div>
                )}

                {verified && (
                    <div className="flex items-center text-fin-primary bg-fin-primary/10 px-4 py-2 rounded-lg border border-fin-primary/20">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                        <span className="font-medium text-sm">Verified Successfully</span>
                    </div>
                )}
            </div>

            {/* Sticky Footer */}
            <div className="w-full bg-fin-surface border-t border-fin-border p-4 pb-6 mt-auto">
                <div className="max-w-sm mx-auto">
                    <p className="text-center text-xs text-fin-slate mb-4 px-2">
                        By proceeding, you agree to the <a href="/privacy" className="text-fin-primary font-medium">Privacy Policy</a> and <a href="/terms" className="text-fin-primary font-medium">Terms of Service</a>.
                    </p>
                    <Button
                        variant="primary"
                        fullWidth
                        onClick={handleProceed}
                        disabled={!verified}
                        className="py-3.5 text-[15px] font-bold"
                    >
                        Proceed
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default VerifyOtp;
